// action/bankActions.js
import { bankAccountsAPI } from './api';
import { toast } from 'react-toastify';

export const bankActions = {
    // ==================== Bank Account CRUD ====================
    
    // Get all bank accounts
    getAccounts: async (params = {}) => {
        try {
            const response = await bankAccountsAPI.list(params);
            
            if (response.data && response.data.accounts) {
                return {
                    success: true,
                    data: response.data.accounts,
                    total: response.data.total,
                    page: response.data.page,
                    limit: response.data.limit,
                    totalPages: response.data.total_pages,
                    summary: response.data.summary
                };
            } else if (Array.isArray(response)) {
                return {
                    success: true,
                    data: response,
                    total: response.length,
                    page: 1,
                    limit: response.length,
                    totalPages: 1
                };
            } else {
                return {
                    success: false,
                    message: 'Invalid API response format',
                    data: []
                };
            }
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch bank accounts',
                data: []
            };
        }
    },

    // Get single bank account
    getAccount: async (id) => {
        try {
            const response = await bankAccountsAPI.get(id);
            
            if (!response) {
                return { success: false, message: 'Bank account not found' };
            }
            
            if (response.success && response.data) {
                return { success: true, data: response.data };
            } else if (response.data) {
                return { success: true, data: response.data };
            } else {
                return { success: true, data: response };
            }
        } catch (error) {
            console.error('Error fetching bank account:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to fetch bank account' 
            };
        }
    },

    // Create bank account
    createAccount: async (accountData) => {
        try {
            // Validate required fields
            if (!accountData.account_name) {
                return { success: false, message: 'Account holder name is required' };
            }
            if (!accountData.bank_name) {
                return { success: false, message: 'Bank name is required' };
            }
            if (!accountData.account_number) {
                return { success: false, message: 'Account number is required' };
            }
            
            const response = await bankAccountsAPI.create(accountData);
            
            if (response && (response.success || response.status || response.id)) {
                const accountId = response.id || response.data?.id;
                
                toast.success('Bank account created successfully');
                return { 
                    success: true, 
                    data: { ...response, id: accountId },
                    message: 'Bank account created successfully'
                };
            } else {
                toast.error(response?.message || 'Failed to create bank account');
                return { 
                    success: false, 
                    message: response?.message || 'Failed to create bank account' 
                };
            }
        } catch (error) {
            console.error('Error creating bank account:', error);
            toast.error('Failed to create bank account');
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to create bank account' 
            };
        }
    },

    // Update bank account
    updateAccount: async (id, accountData) => {
        try {
            if (!id) {
                return { success: false, message: 'Account ID is required' };
            }
            
            accountData.id = id;
            
            const response = await bankAccountsAPI.update(id, accountData);
            
            if (response && (response.success || response.status || response.id)) {
                toast.success('Bank account updated successfully');
                return { 
                    success: true, 
                    data: response,
                    message: 'Bank account updated successfully'
                };
            } else {
                toast.error(response?.message || 'Failed to update bank account');
                return { 
                    success: false, 
                    message: response?.message || 'Failed to update bank account' 
                };
            }
        } catch (error) {
            console.error('Error updating bank account:', error);
            toast.error('Failed to update bank account');
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to update bank account' 
            };
        }
    },

    // Delete bank account
    deleteAccount: async (deleteData) => {
        try {
            if (!deleteData.id && !deleteData) {
                return { success: false, message: 'Account ID is required' };
            }
            
            const id = typeof deleteData === 'object' ? deleteData.id : deleteData;
            const data = { id };
            
            const response = await bankAccountsAPI.delete(data);
            
            if (response && (response.success || response.status || response.deleted)) {
                toast.success(response.message || 'Bank account deleted successfully');
                return { 
                    success: true, 
                    data: response,
                    message: response.message || 'Bank account deleted successfully'
                };
            } else {
                toast.error(response?.message || 'Failed to delete bank account');
                return { 
                    success: false, 
                    message: response?.message || 'Failed to delete bank account' 
                };
            }
        } catch (error) {
            console.error('Error deleting bank account:', error);
            toast.error('Failed to delete bank account');
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to delete bank account' 
            };
        }
    },

    // Set default account
    setDefaultAccount: async (id) => {
        try {
            const response = await bankAccountsAPI.update(id, { is_default: 1 });
            
            if (response && response.success) {
                toast.success('Default account updated');
                return { success: true, data: response };
            } else {
                toast.error(response?.message || 'Failed to set default account');
                return { success: false, message: response?.message };
            }
        } catch (error) {
            console.error('Error setting default account:', error);
            toast.error('Failed to set default account');
            return { success: false, message: error.message };
        }
    },

    // ==================== Bank Transactions ====================

    // Get transactions for an account
    getTransactions: async (accountId, params = {}) => {
        try {
            const response = await bankAccountsAPI.getTransactions(accountId, params);
            
            if (response.data && response.data.transactions) {
                return {
                    success: true,
                    data: response.data.transactions,
                    total: response.data.total,
                    page: response.data.page,
                    limit: response.data.limit,
                    totalPages: response.data.total_pages,
                    summary: response.data.summary
                };
            } else {
                return {
                    success: false,
                    message: 'Invalid API response format',
                    data: []
                };
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch transactions',
                data: []
            };
        }
    },

    // Add transaction
    addTransaction: async (transactionData) => {
        try {
            if (!transactionData.bank_account_id) {
                return { success: false, message: 'Bank account is required' };
            }
            if (!transactionData.transaction_date) {
                return { success: false, message: 'Transaction date is required' };
            }
            if (!transactionData.amount || transactionData.amount <= 0) {
                return { success: false, message: 'Valid amount is required' };
            }
            if (!transactionData.transaction_type) {
                return { success: false, message: 'Transaction type is required' };
            }
            
            const response = await bankAccountsAPI.addTransaction(transactionData);
            
            if (response && response.success) {
                toast.success('Transaction added successfully');
                return { success: true, data: response.data };
            } else {
                toast.error(response?.message || 'Failed to add transaction');
                return { success: false, message: response?.message };
            }
        } catch (error) {
            console.error('Error adding transaction:', error);
            toast.error('Failed to add transaction');
            return { success: false, message: error.message };
        }
    },

    // Update transaction
    updateTransaction: async (id, transactionData) => {
        try {
            const response = await bankAccountsAPI.updateTransaction(id, transactionData);
            
            if (response && response.success) {
                toast.success('Transaction updated successfully');
                return { success: true, data: response.data };
            } else {
                toast.error(response?.message || 'Failed to update transaction');
                return { success: false, message: response?.message };
            }
        } catch (error) {
            console.error('Error updating transaction:', error);
            toast.error('Failed to update transaction');
            return { success: false, message: error.message };
        }
    },

    // Delete transaction
    deleteTransaction: async (id) => {
        try {
            const response = await bankAccountsAPI.deleteTransaction(id);
            
            if (response && response.success) {
                toast.success('Transaction deleted successfully');
                return { success: true, data: response.data };
            } else {
                toast.error(response?.message || 'Failed to delete transaction');
                return { success: false, message: response?.message };
            }
        } catch (error) {
            console.error('Error deleting transaction:', error);
            toast.error('Failed to delete transaction');
            return { success: false, message: error.message };
        }
    },

    // ==================== Bank Transfers ====================

    // Transfer between accounts
    transfer: async (transferData) => {
        try {
            if (!transferData.from_account_id) {
                return { success: false, message: 'Source account is required' };
            }
            if (!transferData.to_account_id) {
                return { success: false, message: 'Destination account is required' };
            }
            if (transferData.from_account_id === transferData.to_account_id) {
                return { success: false, message: 'Source and destination accounts must be different' };
            }
            if (!transferData.amount || transferData.amount <= 0) {
                return { success: false, message: 'Valid amount is required' };
            }
            
            const response = await bankAccountsAPI.transfer(transferData);
            
            if (response && response.success) {
                toast.success('Transfer completed successfully');
                return { success: true, data: response.data };
            } else {
                toast.error(response?.message || 'Failed to transfer');
                return { success: false, message: response?.message };
            }
        } catch (error) {
            console.error('Error transferring:', error);
            toast.error('Failed to transfer');
            return { success: false, message: error.message };
        }
    },

    // Get transfer history
    getTransfers: async (params = {}) => {
        try {
            const response = await bankAccountsAPI.getTransfers(params);
            
            if (response.data && response.data.transfers) {
                return {
                    success: true,
                    data: response.data.transfers,
                    total: response.data.total,
                    page: response.data.page,
                    limit: response.data.limit,
                    totalPages: response.data.total_pages
                };
            } else {
                return {
                    success: false,
                    message: 'Invalid API response format',
                    data: []
                };
            }
        } catch (error) {
            console.error('Error fetching transfers:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch transfers',
                data: []
            };
        }
    },

    // ==================== Bank Reconciliation ====================

    // Reconcile account
    reconcile: async (reconcileData) => {
        try {
            const response = await bankAccountsAPI.reconcile(reconcileData);
            
            if (response && response.success) {
                toast.success('Reconciliation completed successfully');
                return { success: true, data: response.data };
            } else {
                toast.error(response?.message || 'Failed to reconcile');
                return { success: false, message: response?.message };
            }
        } catch (error) {
            console.error('Error reconciling:', error);
            toast.error('Failed to reconcile');
            return { success: false, message: error.message };
        }
    },

    // Get reconciliation report
    getReconciliationReport: async (accountId, params = {}) => {
        try {
            const response = await bankAccountsAPI.getReconciliationReport(accountId, params);
            
            if (response.data) {
                return {
                    success: true,
                    data: response.data
                };
            } else {
                return {
                    success: false,
                    message: 'Invalid API response format'
                };
            }
        } catch (error) {
            console.error('Error fetching reconciliation report:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch reconciliation report'
            };
        }
    },

    // ==================== Dashboard & Reports ====================

// Get bank summary (total balance, account wise) - FIXED for your PHP structure
getSummary: async () => {
    try {
        const response = await bankAccountsAPI.getSummary();
        
        console.log('Raw Summary API Response:', response); // Debug log
        
        // Check if response exists
        if (!response) {
            return {
                success: false,
                message: 'No response from server',
                data: {
                    summary: {
                        total_balance: 0,
                        account_count: 0,
                        total_credits: 0,
                        total_debits: 0
                    }
                }
            };
        }
        
        // Handle successful response
        if (response.success) {
            // Your API returns data in this structure:
            // response.data.overall (for account summary)
            // response.data.monthly (for transaction summary)
            
            const overall = response.data?.overall;
            const monthly = response.data?.monthly;
            
            if (overall) {
                return {
                    success: true,
                    data: {
                        summary: {
                            total_balance: parseFloat(overall.total_balance) || 0,
                            account_count: parseInt(overall.active_accounts) || parseInt(overall.total_accounts) || 0,
                            total_positive: parseFloat(overall.positive_balance) || 0,
                            default_count: parseInt(overall.default_count) || 0,
                            // Include monthly data if available
                            total_credits: monthly ? parseFloat(monthly.total_credits) || 0 : 0,
                            total_debits: monthly ? parseFloat(monthly.total_debits) || 0 : 0,
                            net_flow: monthly ? parseFloat(monthly.net_flow) || 0 : 0
                        },
                        // Also pass through the original data for other uses
                        overall: overall,
                        monthly: monthly,
                        type_breakdown: response.data?.type_breakdown || [],
                        trend: response.data?.trend || [],
                        top_accounts: response.data?.top_accounts || [],
                        recent_transactions: response.data?.recent_transactions || []
                    }
                };
            }
        }
        
        // If we get here, response format is unexpected
        console.warn('Unexpected API response format:', response);
        return {
            success: false,
            message: 'Invalid API response format',
            data: {
                summary: {
                    total_balance: 0,
                    account_count: 0,
                    total_positive: 0,
                    default_count: 0,
                    total_credits: 0,
                    total_debits: 0,
                    net_flow: 0
                }
            }
        };
        
    } catch (error) {
        console.error('Error fetching bank summary:', error);
        return {
            success: false,
            message: error.message || 'Failed to fetch bank summary',
            data: {
                summary: {
                    total_balance: 0,
                    account_count: 0,
                    total_positive: 0,
                    default_count: 0,
                    total_credits: 0,
                    total_debits: 0,
                    net_flow: 0
                }
            }
        };
    }
},
    // Get bank statement
    getStatement: async (accountId, params = {}) => {
        try {
            const response = await bankAccountsAPI.getStatement(accountId, params);
            
            if (response.data) {
                return {
                    success: true,
                    data: response.data
                };
            } else {
                return {
                    success: false,
                    message: 'Invalid API response format'
                };
            }
        } catch (error) {
            console.error('Error fetching bank statement:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch bank statement'
            };
        }
    },

// Get transaction summary for date range - FIXED VERSION
getTransactionSummary: async (params = {}) => {
    try {
        // Use the dedicated transaction summary endpoint
        const response = await bankAccountsAPI.getTransactionSummary(params);
        
        if (response.data) {
            return {
                success: true,
                data: response.data
            };
        } else {
            return {
                success: false,
                message: 'Invalid API response format',
                data: {
                    total_credit: 0,
                    total_debit: 0,
                    net_flow: 0,
                    transaction_count: 0
                }
            };
        }
    } catch (error) {
        console.error('Error fetching transaction summary:', error);
        return {
            success: false,
            message: error.message || 'Failed to fetch transaction summary',
            data: {
                total_credit: 0,
                total_debit: 0,
                net_flow: 0,
                transaction_count: 0
            }
        };
    }
},

    // Format helpers
    formatAccountType: (type) => {
        const types = {
            'savings': 'Savings Account',
            'current': 'Current Account',
            'cash': 'Cash Account',
            'credit_card': 'Credit Card',
            'loan': 'Loan Account'
        };
        return types[type] || type;
    },

    formatCurrency: (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    },

    formatAccountNumber: (accountNumber) => {
        if (!accountNumber) return '';
        // Format: XXXX XXXX XXXX (last 4 digits visible)
        const last4 = accountNumber.slice(-4);
        const masked = 'XXXX XXXX XXXX';
        return `${masked} ${last4}`;
    },
};

export default bankActions;